import React, { useEffect, useRef, useState } from 'react';
import { servicesData } from '../data/mock';
import {
  Lightbulb,
  Sparkles,
  Calendar,
  Users,
  Building,
  Mic,
  Sliders,
  Share2
} from 'lucide-react';

const iconMap = {
  Lightbulb,
  Sparkles,
  Calendar,
  Users,
  Building,
  Mic,
  Sliders,
  Share2
};

const ServicesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const accentColors = [
    'from-[#00C4CC] to-[#00E08F]',
    'from-[#00E08F] to-[#FFF7AE]',
    'from-[#00C4CC] to-[#FFF7AE]',
    'from-[#FF6B7A] to-[#FFF7AE]',
    'from-[#00C4CC] to-[#00E08F]',
    'from-[#00E08F] to-[#FFF7AE]',
    'from-[#00C4CC] to-[#FFF7AE]',
    'from-[#FF6B7A] to-[#00E08F]'
  ];

  return (
    <section ref={sectionRef} id="services" className="py-24 bg-[#F5FDFF] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className={`text-3xl md:text-4xl font-bold text-[#04101C] mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Everything You Need to <span className="bg-gradient-to-r from-[#00C4CC] to-[#00E08F] bg-clip-text text-transparent">Launch</span>
          </h2>
          <p
            className={`text-[#04101C]/60 max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Full-service podcast production from ideation to global distribution. We handle it all.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesData.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className={`group relative bg-white rounded-2xl p-6 border border-[#04101C]/5 hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 cursor-pointer overflow-hidden ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                {/* Accent Bar */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accentColors[index]} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}
                />

                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#00C4CC]/5 to-[#00E08F]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Icon */}
                <div
                  className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${accentColors[index]} flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}
                >
                  <Icon size={24} className="text-[#04101C]" />
                </div>

                {/* Content */}
                <h3 className="relative text-xl font-bold text-[#04101C] mb-3 group-hover:text-[#00C4CC] transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="relative text-[#04101C]/60 text-sm leading-relaxed">
                  {service.description}
                </p>

                {/* Arrow */}
                <div className="relative mt-4 flex items-center text-[#00C4CC] font-medium text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-2">
                  Learn more
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
